import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { gbp, pct } from "../lib/format";
import { invest as investApi } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { TrendingUp, Wallet } from "lucide-react";

export const InvestModal = ({ property, open, onOpenChange, wallet, onSuccess }) => {
  const [amount, setAmount] = useState(5000);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setAmount(5000);
  }, [open, property?.id]);

  const min = property?.min_investment_gbp ?? 5000;
  const max = Math.min(wallet ?? 0, property?.valuation_gbp ?? 100000);
  const sliderMax = Math.max(min, max);

  const projected = useMemo(() => {
    if (!property) return { annual: 0, monthly: 0, tokens: 0 };
    const annual = amount * (property.projected_yield_pct / 100);
    return {
      annual,
      monthly: annual / 12,
      tokens: amount, // £1 per token
    };
  }, [amount, property]);

  if (!property) return null;

  const handleConfirm = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Session expired. Please sign in again.");
      return;
    }
    if (amount < min) {
      toast.error(`Minimum ticket is ${gbp(min)}.`);
      return;
    }
    if (amount > wallet) {
      toast.error("Insufficient wallet balance.");
      return;
    }
    setLoading(true);
    try {
      const res = await investApi(user.id, property.id, amount);
      saveUser({ ...user, wallet_gbp: res.wallet_gbp });
      toast.success(`Confirmed: ${gbp(amount)} allocated to ${property.name}.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Investment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-slate-200 bg-white p-8"
        data-testid="invest-modal"
      >
        <DialogHeader>
          <div className="overline">Invest — Fractional Allocation</div>
          <DialogTitle className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900">
            {property.name}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {property.asset_type} · {property.city} · Projected net yield {pct(property.projected_yield_pct)} p.a.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Wallet className="h-4 w-4 brickstok-gold" /> Available wallet
            </div>
            <div className="font-display text-lg font-semibold text-slate-900 font-mono-num" data-testid="invest-modal-wallet">
              {gbp(wallet)}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label htmlFor="invest-amount" className="text-sm font-medium text-slate-700">
                Investment amount
              </Label>
              <span className="text-xs text-slate-500 font-mono-num">Min {gbp(min)}</span>
            </div>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-num">£</span>
              <Input
                id="invest-amount"
                type="number"
                min={min}
                max={sliderMax}
                step={500}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                className="h-11 pl-7 font-mono-num text-lg font-semibold text-slate-900"
                data-testid="invest-amount-input"
              />
            </div>
            <div className="mt-4">
              <Slider
                value={[amount]}
                onValueChange={(v) => setAmount(v[0])}
                min={min}
                max={sliderMax}
                step={500}
                data-testid="invest-amount-slider"
              />
              <div className="mt-2 flex justify-between text-xs text-slate-500 font-mono-num">
                <span>{gbp(min)}</span>
                <span>{gbp(sliderMax)}</span>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-900 bg-slate-900 p-5 text-white">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="overline" style={{ color: "#94a3b8" }}>Tokens</div>
                <div className="mt-1 font-display text-xl font-semibold font-mono-num" data-testid="invest-preview-tokens">{projected.tokens.toLocaleString()}</div>
              </div>
              <div>
                <div className="overline" style={{ color: "#94a3b8" }}>Monthly Payout</div>
                <div className="mt-1 font-display text-xl font-semibold font-mono-num brickstok-gold" data-testid="invest-preview-monthly">
                  {gbp(projected.monthly, { decimals: 2 })}
                </div>
              </div>
              <div>
                <div className="overline" style={{ color: "#94a3b8" }}>Annual Yield</div>
                <div className="mt-1 flex items-center gap-1 font-display text-xl font-semibold font-mono-num" data-testid="invest-preview-annual">
                  <TrendingUp className="h-4 w-4 brickstok-gold" /> {gbp(projected.annual, { decimals: 0 })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex-row gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="invest-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || amount < min || amount > wallet}
            className="text-slate-900 hover:brightness-95"
            style={{ background: "#D4AF37" }}
            data-testid="invest-confirm-btn"
          >
            {loading ? "Confirming..." : `Confirm ${gbp(amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvestModal;
